import { useEffect, useState } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { Plus, ChevronDown, ChevronUp } from 'lucide-react';

function ClientForm({ onSave, onCancel, initial }) {
  const [f, setF] = useState(initial || { name:'', contact:'', email:'', phone:'', address:'' });
  const set = (k,v) => setF(p=>({...p,[k]:v}));
  const handleSubmit = async e => {
    e.preventDefault();
    try { await onSave(f); } catch(err) { toast.error(err.message); }
  };
  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
      <h3 className="font-bold text-gray-900">{initial ? 'Edit Client' : 'New Client'}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[['name','Company Name *',true],['contact','Primary Contact'],['email','Email'],['phone','Phone'],['address','Address']].map(([k,label,req])=>(
          <div key={k} className={k==='address'?'sm:col-span-2':''}>
            <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
            <input required={req} value={f[k]} onChange={e=>set(k,e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">{initial?'Save':'Create'}</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-200 text-sm rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
      </div>
    </form>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => { load(); }, []); // eslint-disable-line
  const load = () => api.get('/opsive/clients').then(setClients).catch(e=>toast.error(e.message));

  const create = async f => { await api.post('/opsive/clients', f); toast.success('Client added'); setCreating(false); load(); };
  const save   = async f => { await api.patch(`/opsive/clients/${editing.client_id}`, f); toast.success('Updated'); setEditing(null); load(); };
  const remove = async id => { if(!window.confirm('Delete client?'))return; await api.delete(`/opsive/clients/${id}`); toast.success('Deleted'); load(); };
  const addNote = async id => {
    if(!note.trim())return;
    await api.patch(`/opsive/clients/${id}`, { note });
    setNote(''); load(); toast.success('Note added');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Clients</h1>
        <button onClick={()=>setCreating(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700">
          <Plus className="w-4 h-4"/> New Client
        </button>
      </div>
      {creating && <ClientForm onSave={create} onCancel={()=>setCreating(false)} />}
      <div className="space-y-2">
        {clients.map(c => (
          <div key={c.client_id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {editing?.client_id === c.client_id ? (
              <div className="p-4"><ClientForm initial={c} onSave={save} onCancel={()=>setEditing(null)} /></div>
            ) : (
              <>
                <div className="px-5 py-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.contact} {c.email?`· ${c.email}`:''} {c.phone?`· ${c.phone}`:''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={()=>setEditing(c)} className="text-xs text-blue-600 hover:underline">Edit</button>
                    <button onClick={()=>remove(c.client_id)} className="text-xs text-red-500 hover:underline">Delete</button>
                    <button onClick={()=>setExpanded(expanded===c.client_id?null:c.client_id)} className="text-gray-400">
                      {expanded===c.client_id?<ChevronUp className="w-4 h-4"/>:<ChevronDown className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
                {expanded===c.client_id && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50 space-y-3">
                    {c.address && <p className="text-sm text-gray-600">{c.address}</p>}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Notes</p>
                      <div className="space-y-1 mb-2">
                        {(c.notes||[]).map(n=>(
                          <div key={n.note_id} className="bg-white rounded-lg px-3 py-2 text-sm text-gray-700 border border-gray-100">
                            {n.text} <span className="text-xs text-gray-400 ml-2">— {n.author}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Add a note…"
                          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"/>
                        <button onClick={()=>addNote(c.client_id)}
                          className="text-sm px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700">Save</button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {clients.length===0&&!creating&&<div className="text-center py-16 text-gray-400 text-sm">No clients yet.</div>}
      </div>
    </div>
  );
}
